import isNil from 'lodash/isNil';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import { WidgetMeta, WidgetProps } from '../../types/WidgetProps';
import { BreakdownTypes, PaymentBreakdown } from '../../types/PaymentBreakdown';
import { ShippingMethod } from '../../types/Shipping';
import { OrderItem } from '../../types/OrderItem';
import { OnClick } from '../../types/ExternalContract';
import { ButtonStyle } from '../../types/Styles';
import { getBreakdownTotal, getOrderItemsTotal, isFalsy, isValidAmount } from './utils/amount';
import { requiredProps } from './props';

export interface ValidationResult {
  isError: boolean;
  message: string;
  payload?: any;
}

export function validateRequiredProps(props: WidgetProps): ValidationResult {
  const requiredPropsMissing = requiredProps.filter((p) => isFalsy(props[p]));
  const isError = requiredPropsMissing.length !== 0;
  return {
    isError,
    message: `Required props are missing for <ExpressCheckoutWidget />: ${requiredPropsMissing.join(', ')}`,
    payload: requiredPropsMissing,
  };
}

export function validateMetaProps(meta: WidgetMeta): ValidationResult {
  if (!meta) {
    return { isError: true, message: 'Required props are missing for <ExpressCheckoutWidget />: meta' };
  }

  let isError;
  let message: string;
  if (isFalsy(meta.appDefId)) {
    isError = true;
    message = `Required meta prop appDefId is missing for <ExpressCheckoutWidget />`;
  }
  if (isFalsy(meta.appInstanceId)) {
    isError = true;
    message = `Required meta prop appInstanceId is missing for <ExpressCheckoutWidget />`;
  }

  return { isError, message };
}

export function validatePaymentBreakdownAndOrderItems(
  paymentBreakdown: PaymentBreakdown,
  orderItems: OrderItem[] = [],
  paymentAmount: string,
): ValidationResult {
  if (!paymentBreakdown) {
    return { isError: false, message: '' };
  }

  if (!orderItems.length) {
    const isError = true;
    const message = `Order items should be passed along with payment breakdown to <ExpressCheckoutWidget />`;

    return { isError, message, payload: { orderItems } };
  }

  // validate paymentBreakdown amounts
  const breakdownTypes = Object.keys(paymentBreakdown);
  const invalidBreakdownItems = breakdownTypes
    .filter((type) => !isValidAmount(paymentBreakdown[type]))
    .reduce<Record<string, string>>((acc, type) => {
      acc[type] = paymentBreakdown[type];
      return acc;
    }, {});

  const hasInvalidBreakdownItems = Object.keys(invalidBreakdownItems).length !== 0;

  if (hasInvalidBreakdownItems) {
    const message = `Payment breakdown items passed to <ExpressCheckoutWidget /> are invalid: ${JSON.stringify(
      invalidBreakdownItems,
    )}`;

    return { isError: true, message, payload: { paymentBreakdown } };
  }

  // validate orderItems amounts
  const hasInvalidOrderItems = orderItems.some((item) => !isValidAmount(item.price));
  if (hasInvalidOrderItems) {
    const message = `Order items passed to <ExpressCheckoutWidget /> should have a valid price: ${JSON.stringify(
      orderItems,
    )}`;

    return { isError: true, message, payload: { orderItems } };
  }

  // validate paymentBreakdown total
  const isInvalidTotal = getBreakdownTotal(paymentBreakdown) !== Number(paymentAmount);
  if (isInvalidTotal) {
    const message = `Payment breakdown total amount passed to <ExpressCheckoutWidget /> should match payment amount`;
    return { isError: true, message, payload: { paymentAmount, orderItems, paymentBreakdown } };
  }

  // validate orderItems subtotal
  // paymentBreakdown.itemsTotal should be equal sum of all order items: sum(price * quantity)
  const isInvalidItemsTotal = getOrderItemsTotal(orderItems) !== Number(paymentBreakdown[BreakdownTypes.ItemsTotal]);
  if (isInvalidItemsTotal) {
    const message = `Payment breakdown items total passed to <ExpressCheckoutWidget /> should match sum of all order items amounts`;

    return { isError: true, message, payload: { paymentAmount, orderItems, paymentBreakdown } };
  }
  return { isError: false, message: '' };
}

export function validatePaymentAmount(paymentAmount: string): ValidationResult {
  if (!isValidAmount(paymentAmount)) {
    return {
      isError: true,
      message: `Payment amount passed to <ExpressCheckoutWidget /> is invalid: ${paymentAmount}`,
      payload: { paymentAmount },
    };
  }
  const MAX_PAYPAL_AMOUNT = 9999999.99;
  const MIN_PAYPAL_AMOUNT = 0.01;
  const amount = Number(paymentAmount);

  if (amount > MAX_PAYPAL_AMOUNT || amount < MIN_PAYPAL_AMOUNT) {
    return {
      isError: true,
      message: `Payment amount is not valid for <ExpressCheckoutWidget /> : ${paymentAmount}. Should be less than "9999999.99" and > "0".`,
    };
  }
  return {
    isError: false,
    message: '',
  };
}

export function validateButtonStyle(buttonStyle: ButtonStyle): ValidationResult {
  const isValid = isNumber(buttonStyle.height) && buttonStyle.height >= 40 && buttonStyle.height <= 55;
  return {
    isError: !isValid,
    message: !isValid ? `<ExpressCheckoutWidget /> "buttonStyle.height" expected to be between 40px and 55px` : '',
  };
}

export const validateLocale = (locale: string, shouldLogError = true): ValidationResult => {
  const isError = isFalsy(locale);

  if (shouldLogError && isError) {
    console.error('Required props are missing for <ExpressCheckoutWidget />: locale');
  }

  return { isError, message: 'Required props are missing for <ExpressCheckoutWidget />: locale' };
};

export const validateOnClick = (onClick: OnClick): ValidationResult => {
  const isError = false;
  if (!isFunction(onClick) && !isNil(onClick)) {
    return {
      isError: true,
      message: 'onClick is not a function',
    };
  }
  return {
    isError,
    message: '',
  };
};

export function validateShippingProps({
  requestShipping,
  shippingMethods,
  onShippingContactSelected,
  onShippingMethodSelected,
}): ValidationResult {
  if (!requestShipping) {
    return {
      isError: false,
      message: '',
    };
  }

  // shipping methods can be passed later, after onShippingContactSelected
  if (shippingMethods && Array.isArray(shippingMethods)) {
    const invalidShippingMethods = shippingMethods.filter((method) => !isShippingMethodValid(method));

    if (invalidShippingMethods.length !== 0) {
      const isError = true;
      const message = `Shipping methods passed to <ExpressCheckoutWidget /> are invalid: ${JSON.stringify(
        invalidShippingMethods,
      )}`;

      return { isError, message, payload: { shippingMethods } };
    }
  }

  if (onShippingMethodSelected && 'function' !== typeof onShippingMethodSelected) {
    return {
      isError: true,
      message:
        '"onShippingMethodSelected" callback must be provided to <ExpressCheckoutWidget /> if "requestShipping" is "true"',
    };
  }

  if (!onShippingContactSelected) {
    return {
      isError: true,
      message:
        '"onShippingContactSelected" callback must be provided to <ExpressCheckoutWidget /> if "requestShipping" is "true"',
    };
  }

  return {
    isError: false,
    message: '',
  };
}

export const validateWidgetProps = (props: WidgetProps, shouldLogError = true): ValidationResult => {
  const failedValidations: ValidationResult[] = [
    validateRequiredProps(props),
    validateMetaProps(props.meta),
    validatePaymentBreakdownAndOrderItems(props.paymentBreakdown, props.orderItems, props.paymentAmount),
    validateButtonStyle(props.buttonStyle),
    validatePaymentAmount(props.paymentAmount),
    validateShippingProps({
      requestShipping: props.requestShipping,
      shippingMethods: props.shippingMethods,
      onShippingContactSelected: props.onShippingContactSelected,
      onShippingMethodSelected: props.onShippingMethodSelected,
    }),
    validateOnClick(props.onClick),
  ].filter(({ isError }) => isError);

  if (shouldLogError) {
    failedValidations.forEach(({ message }) => {
      console.error(message);
    });
  }

  // special case for locale
  validateLocale(props.locale, shouldLogError);

  return {
    isError: failedValidations.length > 0,
    message: '',
  };
};

const isShippingMethodValid = (method: ShippingMethod) => {
  return (
    !isFalsy(method.label) && !isFalsy(method.detail) && isValidAmount(method.amount) && !isFalsy(method.identifier)
  );
};
